/**
 * AttachmentView — renders an attachment's content for the input-detail modal.
 *
 * MIME-driven dispatch:
 *   - text/markdown · text/plain · text/csv · application/json
 *       fetch the bytes and hand off to ArtifactView (markdown rendering,
 *       CSV table, JSON pretty-print, plain text).
 *   - image/*       <img> with the inline download URL
 *   - application/pdf  <embed> with the inline download URL
 *   - everything else  download-only fallback message
 *
 * HTML is NOT handled here — the parent intercepts HTML attachments and
 * opens them directly in a new browser tab (see JobDetailPage Inputs).
 */
import { useEffect, useState } from 'react';
import { attachmentInlineUrl } from '@/features/attachments';
import { t } from '@/lib/i18n';
import type { JobAttachment } from '../schemas';
import { ArtifactView } from './ArtifactView';
import styles from './AttachmentView.module.css';

const TEXT_LIKE_MIMES = new Set(['text/markdown', 'text/plain', 'text/csv', 'application/json']);

export interface AttachmentViewProps {
  tenantId: string;
  attachment: JobAttachment;
}

export function AttachmentView({ tenantId, attachment }: AttachmentViewProps) {
  const url = attachmentInlineUrl(tenantId, attachment.id);

  if (TEXT_LIKE_MIMES.has(attachment.mime_type)) {
    return <TextAttachmentBody url={url} attachment={attachment} />;
  }
  if (attachment.mime_type.startsWith('image/')) {
    return <img src={url} alt={attachment.name} className={styles.image} />;
  }
  if (attachment.mime_type === 'application/pdf') {
    return <embed src={url} type="application/pdf" className={styles.pdf} />;
  }
  return (
    <div className={styles.binary}>
      {t('{name} is a binary file ({mime}). Use Download to save it locally.', {
        name: attachment.name,
        mime: attachment.mime_type,
      })}
    </div>
  );
}

function TextAttachmentBody({ url, attachment }: { url: string; attachment: JobAttachment }) {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setContent(null);
    setError(null);
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((text) => {
        if (!cancelled) setContent(text);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (error) {
    return (
      <div className={styles.errorBox}>{t('Could not load attachment: {msg}', { msg: error })}</div>
    );
  }
  if (content === null) {
    return <div className={styles.loading}>{t('Loading…')}</div>;
  }
  // Reuse ArtifactView by shaping the attachment as an Artifact. id +
  // turn_number + created_at aren't used by ArtifactView's rendering path
  // for text mime types — only mime_type + content matter.
  return (
    <ArtifactView
      artifact={{
        id: attachment.id,
        turn_number: 0,
        parameter_name: attachment.name,
        mime_type: attachment.mime_type,
        size_bytes: attachment.size_bytes,
        content,
        created_at: attachment.createdAt,
      }}
    />
  );
}
